/**
 * @file Scrollable chat transcript for the agent panel. Renders user and
 * assistant messages, image and tabular attachments, agentic finish tags,
 * and inline approval prompts driven by `agentApprovalState`. Approvals
 * carrying a `review` payload render a compact card whose only actions are
 * Cancel and "Open review" — Approve lives solely in the Mapping Review
 * panel so unseen edits can never be approved from the chat. After a
 * resolution the decision shows as an in-memory "Approved"/"Cancelled" card
 * off `agentApprovalState.resolved` that survives only until the next loop
 * start / clear. The transcript comes from `AITranscriptContext`, split from
 * `AIContext` so a message write cannot churn the action surface. Loop progress
 * comes from `agenticProgress` (AIContext): spinner, iteration count, and a
 * collapsed list of recent tool display names via `toolDisplayName`, falling
 * back to the raw name. Storage refs resolve to download URLs on demand. Each
 * settled row is a memoized `TranscriptMessage`, so a streamed token — which
 * changes only `activeMessageContent` — never rebuilds the transcript. An append
 * is the exception: the store replaces the whole `messages` array, so every row
 * rebuilds once. The memo pays on the streaming path, not on transcript growth.
 * An answer that ran analytics queries ends with a collapsed `AnalyticsQueriesPanel`
 * read from its own `analyticsQueries`.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, theme, Tag, Spin, Image, Button, Tooltip } from 'antd';
import { UserOutlined, DeploymentUnitOutlined, PaperClipOutlined } from '@ant-design/icons';
import ReactMarkdown, { Components } from 'react-markdown';
import { ref, getDownloadURL } from 'firebase/storage';
import { useSnapshot } from 'valtio';

import { AgentMarkdownFontScale, toolDisplayName, type ApprovalReason } from '#types';

import { AIContext, type ChatMessage, type ImageFileAttachment } from '../../providers/AIContext';
import { AITranscriptContext } from '../../providers/AITranscriptContext';
import { MessageContext } from '../../providers/MessageContext';
import { storage } from '../../services/startStorage';
import { callRunLanguageModel } from '../../services/cloudRun';
import FlexBox from '../atoms/FlexBox';
import FlexCol from '../atoms/FlexCol';
import { agentApprovalState } from './agentApprovalState';
import AnalyticsQueriesPanel from './AnalyticsQueriesPanel';
import { closeMappingReview, openMappingReview } from './review/mappingReviewSession';
import { UserContext } from '../../providers/UserContext';
import { useAgentContext } from '../../use/useAgentContext';

const { useToken } = theme;
const { Text } = Typography;

const finishStatusColor: Record<string, string> = {
  success: 'green',
  failure: 'red',
  partial: 'orange',
};

const AgenticFinishBadge: React.FC<{ message: ChatMessage }> = ({ message: msg }) => {
  const finish = msg.agenticFinish;
  if (!finish) return null;

  return (
    <FlexBox noGrow gap={5} style={{ marginTop: 5 }}>
      <Tag color={finishStatusColor[finish.status] ?? 'default'}>
        {finish.status}
      </Tag>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {finish.totalIterations} iteration{finish.totalIterations !== 1 ? 's' : ''}, {finish.totalToolCalls} tool call{finish.totalToolCalls !== 1 ? 's' : ''}
      </Text>
    </FlexBox>
  );
};

const APPROVAL_REASON_LABEL: Record<ApprovalReason, string> = {
  write: 'write',
  destructive: 'destructive',
  bulk: 'bulk',
  permanent: 'permanent',
  external: 'external call',
};

const APPROVAL_REASON_COLOR: Record<ApprovalReason, string> = {
  write: 'blue',
  destructive: 'red',
  bulk: 'orange',
  permanent: 'purple',
  external: 'gold',
};

function recentToolLabels(labels: string[]): string[] {
  const collapsed = labels.reduce<{ label: string; count: number }[]>((acc, label) => {
    const last = acc[acc.length - 1];
    if (last && last.label === label) {
      last.count += 1;
    } else {
      acc.push({ label, count: 1 });
    }
    return acc;
  }, []);
  return collapsed
    .slice(-5)
    .map(({ label, count }) => (count > 1 ? `${label} ×${count}` : label));
}

const ResolvedApproval: React.FC = () => {
  const { token } = useToken();
  const snap = useSnapshot(agentApprovalState);
  const resolved = snap.resolved;

  if (!resolved) return null;

  const countEntries = Object.entries(resolved.counts ?? {});

  return (
    <FlexBox alignStart style={{ marginTop: 10, minWidth: 0, width: '100%' }} noGrow>
      <FlexBox noGrow style={{ color: token.colorTextSecondary, marginTop: 4 }}>
        <DeploymentUnitOutlined />
      </FlexBox>
      <FlexCol
        noGap
        stretch
        style={{
          minWidth: 0,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadius,
          padding: 8,
        }}
      >
        <FlexBox noGrow justifyStart gap={5}>
          <Tag color={resolved.approved ? 'green' : 'default'} style={{ margin: 0 }}>
            {resolved.approved ? 'Approved' : 'Cancelled'}
          </Tag>
        </FlexBox>
        <Text style={{ fontSize: token.fontSize * AgentMarkdownFontScale.p, marginTop: 2 }}>
          {resolved.summary}
        </Text>
        {countEntries.length > 0 && (
          <Text type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
            {countEntries.map(([k, v]) => `${v} ${k}`).join(' · ')}
          </Text>
        )}
      </FlexCol>
    </FlexBox>
  );
};

const InlineApproval: React.FC = () => {
  const { token } = useToken();
  const snap = useSnapshot(agentApprovalState);
  const { messageApi } = useContext(MessageContext);
  const { user } = useContext(UserContext);
  const { getHasNavigatedAway } = useAgentContext();
  const pending = snap.pending;
  const submitting = snap.submitting;

  if (!pending) return null;

  const hasReview = Boolean(pending.review);

  const resolve = async (approved: boolean) => {
    if (agentApprovalState.submitting) return;
    const { traceId, toolCallId } = pending;
    agentApprovalState.submitting = true;
    try {
      await callRunLanguageModel({
        approvalResolved: {
          traceId,
          toolCallId,
          approved,
          ...(getHasNavigatedAway() ? { userNavigatedAway: true } : {}),
        },
      });
      agentApprovalState.resolved = {
        toolCallId,
        summary: pending.summary,
        approved,
        ...(pending.counts ? { counts: { ...pending.counts } } : {}),
      };
      agentApprovalState.pending = null;
      agentApprovalState.submitting = false;
      closeMappingReview();
    } catch (e) {
      agentApprovalState.submitting = false;
      const errorMessage = e instanceof Error ? e.message : 'Failed to send approval response.';
      messageApi?.error?.(errorMessage);
      console.error('Failed to send approvalResolved:', e);
    }
  };

  const openReview = () => {
    const live = agentApprovalState.pending;
    if (live && user?.uid) openMappingReview(live, user.uid);
  };

  const countEntries = Object.entries(pending.counts ?? {});

  return (
    <FlexBox alignStart style={{ marginTop: 10, minWidth: 0, width: '100%' }} noGrow>
      <FlexBox
        noGrow
        style={{
          color: token.colorWarningText,
          marginTop: 4,
        }}
      >
        <DeploymentUnitOutlined />
      </FlexBox>
      <FlexCol
        noGap
        stretch
        style={{
          minWidth: 0,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: token.borderRadius,
          padding: 8,
          background: token.colorFillQuaternary,
        }}
      >
        <Text strong style={{ fontSize: token.fontSize * AgentMarkdownFontScale.p }}>
          Approval required
        </Text>
        {pending.toolName && (
          <Text type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
            {toolDisplayName(pending.toolName)}
          </Text>
        )}
        <Text style={{ fontSize: token.fontSize * AgentMarkdownFontScale.p, marginTop: 2 }}>
          {pending.summary}
        </Text>
        {countEntries.length > 0 && (
          <Text type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
            {countEntries.map(([k, v]) => `${v} ${k}`).join(' · ')}
          </Text>
        )}
        {pending.preview && (
          <>
            <Text type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
              {pending.preview.rowsIn} rows in · up to {pending.preview.willWrite} will write
            </Text>
            {pending.preview.tagCounts
              && (pending.preview.tagCounts.summary > 0 || pending.preview.tagCounts.junk > 0) && (
              <Text type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
                {pending.preview.tagCounts.value} value · {pending.preview.tagCounts.summary} summary · {pending.preview.tagCounts.junk} junk
              </Text>
            )}
            {pending.preview.truncated && (
              <Text type="warning" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
                truncated to {pending.preview.willWrite}
              </Text>
            )}
            {pending.preview.unmappedSourceColumns.length > 0 && (
              <Text type="warning" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small, marginTop: 2 }}>
                Unmapped columns: {pending.preview.unmappedSourceColumns.join(', ')}
              </Text>
            )}
          </>
        )}
        {pending.reasons.length > 0 && (
          <FlexBox noGrow wrap gap={4} style={{ marginTop: 5 }}>
            {pending.reasons.map((r) => (
              <Tag key={r} color={APPROVAL_REASON_COLOR[r]} style={{ margin: 0 }}>
                {APPROVAL_REASON_LABEL[r]}
              </Tag>
            ))}
          </FlexBox>
        )}
        <FlexBox noGrow stretch justifyEnd style={{ marginTop: 5, marginBottom: 10 }}>
          <Tooltip title={submitting ? 'A resolution is already in progress' : undefined}>
            <Button
              size="small"
              onClick={() => resolve(false)}
              disabled={submitting}
              aria-label="Cancel agent action"
            >
              Cancel
            </Button>
          </Tooltip>
          {hasReview ? (
            <Button
              size="small"
              type="primary"
              onClick={openReview}
              disabled={submitting}
              aria-label="Open mapping review"
            >
              Open review
            </Button>
          ) : (
            <Button
              size="small"
              type="primary"
              onClick={() => resolve(true)}
              loading={submitting}
              disabled={submitting}
              aria-label="Approve agent action"
            >
              Approve
            </Button>
          )}
        </FlexBox>
      </FlexCol>
    </FlexBox>
  );
};

const downloadUrlCache = new Map<string, Promise<string>>();

function getCachedDownloadUrl(storagePath: string): Promise<string> {
  let pending = downloadUrlCache.get(storagePath);
  if (!pending) {
    pending = getDownloadURL(ref(storage, storagePath)).catch((e) => {
      downloadUrlCache.delete(storagePath);
      throw e;
    });
    downloadUrlCache.set(storagePath, pending);
  }
  return pending;
}

const ImageThumbnail: React.FC<{ attachment: ImageFileAttachment }> = ({ attachment }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let cancelled = false;
    getCachedDownloadUrl(attachment.originalPath)
      .then((u) => { if (!cancelled) setUrl(u); })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; };
  }, [attachment.originalPath, isVisible]);

  return (
    <div ref={containerRef} style={{ width: 80, height: 80 }}>
      {url ? (
        <Image
          src={url}
          alt={attachment.filename}
          width={80}
          height={80}
          style={{ objectFit: 'cover', borderRadius: 4 }}
        />
      ) : null}
    </div>
  );
};

const AttachmentPill: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const atts = message.attachments;
  if (!atts?.length) return null;
  return (
    <FlexBox 
      noGrow 
      wrap 
      gap={5} 
      style={{ 
        maxWidth: '100%',
        marginTop: 5, 
      }}
    >
      {atts.map((att, i) => {
        if (att.kind === 'image') {
          return <ImageThumbnail key={`${att.filename}-${i}`} attachment={att} />;
        }
        return (
          <Tag
            key={`${att.filename}-${i}`}
            icon={<PaperClipOutlined />}
            style={{
              maxWidth: '100%',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              minWidth: 0,
            }}
            color="gray"
          >
            {att.filename}
          </Tag>
        );
      })}
    </FlexBox>
  );
};

/**
 * Renders agent markdown with the transcript's heading and paragraph scale. Each
 * instance memoizes its own `Components` map on the theme token, so a row that
 * re-renders — or a streamed token — never rebuilds one. Holding the map here
 * rather than in `MessagesList` is what lets the row memo work: a map built in
 * the list body would be a fresh prop on every keystroke of the stream.
 */
const AgentMarkdown: React.FC<{ children: string | null | undefined }> = ({ children }) => {
  const { token } = useToken();

  const components = useMemo<Components>(() => {
    const headingBase: React.CSSProperties = {
      fontWeight: 600,
      color: token.colorTextHeading,
      margin: 0,
    };
    return {
      p: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLParagraphElement>) => (
        <p style={{ margin: 0, fontSize: token.fontSize * AgentMarkdownFontScale.p }} {...props}>
          {children}
        </p>
      ),
      h1: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h1 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h1 }} {...props}>{children}</h1>
      ),
      h2: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h2 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h2 }} {...props}>{children}</h2>
      ),
      h3: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h3 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h3 }} {...props}>{children}</h3>
      ),
      h4: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h4 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h4 }} {...props}>{children}</h4>
      ),
      h5: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h5 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h5 }} {...props}>{children}</h5>
      ),
      h6: ({ node, children, ...props }: React.PropsWithChildren<{ node?: any }> & React.HTMLAttributes<HTMLHeadingElement>) => (
        <h6 style={{ ...headingBase, fontSize: token.fontSize * AgentMarkdownFontScale.h6 }} {...props}>{children}</h6>
      ),
    };
  }, [token]);

  return <ReactMarkdown components={components}>{children}</ReactMarkdown>;
};

/** Sender glyph in front of a transcript row: user green, agent blue. */
const SenderIcon: React.FC<{ sender: string }> = ({ sender }) => {
  const { token } = useToken();

  return (
    <FlexBox
      noGrow
      style={{
        color: sender === 'user' ? token.colorSuccessText : token.blue,
        marginTop: 4,
      }}
    >
      {sender === 'user' ? <UserOutlined /> : <DeploymentUnitOutlined />}
    </FlexBox>
  );
};

/** One settled transcript row. Memoized: a streamed token must not rebuild it. */
const TranscriptMessage: React.FC<{ message: ChatMessage }> = React.memo(({ message }) => (
  <FlexBox
    alignStart
    style={{ marginTop: 10, minWidth: 0, width: '100%' }}
    noGrow
  >
    <SenderIcon sender={message.sender} />
    <FlexCol noGap stretch style={{ minWidth: 0 }}>
      <FlexBox stretch noGrow>
        <Text>
          {message.sender === 'user' ?
            <b>You</b>
            :
            <b>System</b>
          }
          <AgentMarkdown>{message.content}</AgentMarkdown>
        </Text>
      </FlexBox>
      <AttachmentPill message={message} />
      <AgenticFinishBadge message={message} />
      {message.analyticsQueries?.length ?
        <AnalyticsQueriesPanel messageId={message.id} analyticsQueries={message.analyticsQueries} />
        : null}
    </FlexCol>
  </FlexBox>
));

TranscriptMessage.displayName = 'TranscriptMessage';

const MessagesList: React.FC = () => {
  const { displayMessages } = useContext(AITranscriptContext);
  const {
    activeMessageContent,
    isLoading,
    agenticProgress,
  } = useContext(AIContext);
  const { token } = useToken();
  const approvalSnap = useSnapshot(agentApprovalState);
  const bottomRef = useRef<HTMLDivElement>(null);
  const lastMessage = displayMessages[displayMessages.length - 1];

  useEffect(() => {
    const id = requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ block: 'end' });
    });
    return () => cancelAnimationFrame(id);
  }, [
    displayMessages.length,
    lastMessage?.content,
    activeMessageContent,
    isLoading,
    agenticProgress?.isRunning,
    agenticProgress?.iteration,
    agenticProgress?.toolCalls,
    approvalSnap.pending?.toolCallId,
  ]);

  return (
    <FlexCol
      stretch
      justifyEnd
      alignStart
      gap={0}
      style={{
        minWidth: 240,
      }}
    >
      <FlexCol
        noGrow
        style={{
          width: '100%',
          marginBottom: 0,
          paddingBottom: 0,
        }}
        gap={0}
        id='messages-list'
      >
        <FlexCol noGrow noGap style={{ marginTop: 10 }}>
          <Text style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small }}>
            The System Agent makes mistakes. You are responsible for all results it produces. Verify every output before acting on it.
          </Text >
          <Text type="danger" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small }}>All activity is monitored.</Text>
        </FlexCol>
        {displayMessages.map((item) => (
          <TranscriptMessage key={item.id} message={item} />
        ))}
      </FlexCol>
      <FlexBox alignStart noGrow>
        {(isLoading || activeMessageContent || agenticProgress) && <SenderIcon sender="agent" />}
        <FlexCol noGap stretch>
          {agenticProgress?.isRunning && (
            <>
              <Text type="secondary" style={{ marginTop: 2 }}>
                <Spin size="small" /> Iteration {agenticProgress.iteration}/{agenticProgress.maxIterations}
                {agenticProgress.toolCalls > 0 && ` | ${agenticProgress.toolCalls} tool call${agenticProgress.toolCalls !== 1 ? 's' : ''}`}
              </Text>
              {recentToolLabels(agenticProgress.toolLabels).map((label, i) => (
                <Text key={`${label}-${i}`} type="secondary" style={{ fontSize: token.fontSize * AgentMarkdownFontScale.small }}>
                  {label}
                </Text>
              ))}
            </>
          )}
          <FlexBox stretch noGrow>
            <Text 
              type='secondary' 
              style={{ 
                fontSize: token.fontSize * AgentMarkdownFontScale.small,
                color: token.colorTextSecondary, 
              }}
            >
              <AgentMarkdown>{activeMessageContent}</AgentMarkdown>
            </Text>
          </FlexBox>
        </FlexCol>
      </FlexBox>
      <ResolvedApproval />
      <InlineApproval />
      <div ref={bottomRef} />
    </FlexCol>
  );
};

export default MessagesList;
